/**
 * Approval Resource Hooks
 *
 * Type-safe hooks for approval-related API operations.
 * Built on top of the generic API hooks with approval-specific defaults.
 */

import type { paths } from '@access-cms/api-types';
import { type ApprovalFilter, ResourceType } from '@access-cms/shared-constants';

import { queryKeys } from '../../queryKeys';
import type { QueryParamRecord } from '../../types';
import { useApiAction, useApiCreate, useApiDelete } from '../useApiMutation';
import { useApiGet, useApiList } from '../useApiQuery';

// =============================================================================
// Type Aliases
// =============================================================================

type ApprovalListResponse = paths['/api/v1/approvals']['get']['responses']['200']['content']['application/json'];
type ApprovalResponse =
  paths['/api/v1/approvals/{approvalId}']['get']['responses']['200']['content']['application/json'];

type Approval = NonNullable<ApprovalResponse['data']>;

/**
 * Filter parameters for the approvals list endpoint.
 * These are defined locally because the OpenAPI spec does not expose query params
 * (the route uses a validation middleware rather than Fastify schema querystring).
 */
interface ApprovalListFilters {
  applicationId?: string;
  contractId?: string;
  filter?: ApprovalFilter;
  keyword?: string;
  limit?: number;
  page?: number;
  sort?:
    | 'applicant_eua_id'
    | 'applicant_first_name'
    | 'applicant_last_name'
    | 'application_status'
    | 'assigned_at'
    | 'contract_number'
    | 'due_at'
    | 'status';
  order?: 'asc' | 'desc';
  stage?: string;
}

// =============================================================================
// Query Hooks
// =============================================================================

interface UseApprovalsQueryOptions {
  enabled?: boolean;
  filters?: ApprovalListFilters;
}

/**
 * Fetch a paginated list of approvals visible to the caller
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useApprovalsQuery({
 *   filters: { stage: 'COR', limit: 20 },
 * });
 * ```
 */
export function useApprovalsQuery(options?: UseApprovalsQueryOptions) {
  const { filters, enabled } = options ?? {};

  return useApiList('/api/v1/approvals', {
    enabled,
    queryKey: queryKeys[ResourceType.APPROVALS].list(filters as QueryParamRecord),
    queryParams: filters as unknown as undefined,
  });
}

interface UseApprovalQueryOptions {
  enabled?: boolean;
}

/**
 * Fetch a single approval by ID
 *
 * @example
 * ```tsx
 * const { data: approval, isLoading } = useApprovalQuery(approvalId);
 * ```
 */
export function useApprovalQuery(approvalId: string, options?: UseApprovalQueryOptions) {
  return useApiGet(
    '/api/v1/approvals/{approvalId}',
    { approvalId },
    {
      enabled: options?.enabled ?? !!approvalId,
      queryKey: queryKeys[ResourceType.APPROVALS].detail(approvalId),
    },
  );
}

// =============================================================================
// Mutation Hooks
// =============================================================================

interface UseDecideApprovalOptions {
  approvalId: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Record a decision on an approval (APPROVE, REJECT, RETURN)
 *
 * @example
 * ```tsx
 * const decide = useDecideApproval({
 *   approvalId,
 *   onSuccess: () => toast.success('Decision recorded'),
 * });
 *
 * decide.mutate({ action: 'APPROVE' });
 * ```
 */
export function useDecideApproval(options: UseDecideApprovalOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/approvals/{approvalId}/status',
    pathParams: { approvalId: options.approvalId },
  });
}

interface UseClaimApprovalOptions {
  approvalId: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Claim an FNMS pool approval
 *
 * @example
 * ```tsx
 * const claim = useClaimApproval({
 *   approvalId,
 *   onSuccess: () => toast.success('Approval claimed'),
 * });
 *
 * claim.mutate();
 * ```
 */
export function useClaimApproval(options: UseClaimApprovalOptions) {
  return useApiAction({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/approvals/{approvalId}/assignee',
    pathParams: { approvalId: options.approvalId },
  });
}

interface UseUnclaimApprovalOptions {
  approvalId: string;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Release (unclaim) a claimed FNMS approval back to the pool.
 *
 * The caller must be the current claimant or an admin. The FNMS visit (if any) is
 * preserved server-side, so a later re-claim + Send to FNMS inherits the same visit.
 *
 * @example
 * ```tsx
 * const unclaim = useUnclaimApproval({
 *   approvalId,
 *   onSuccess: () => toast.success('Released back to the pool'),
 * });
 *
 * unclaim.mutate({});
 * ```
 */
export function useUnclaimApproval(options: UseUnclaimApprovalOptions) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/approvals/{approvalId}/assignee',
    pathParams: { approvalId: options.approvalId },
  });
}

// =============================================================================
// Exports
// =============================================================================

export type { Approval, ApprovalListFilters, ApprovalListResponse, ApprovalResponse };
